
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link,
    Button
} from "@mui/material";

import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import ConfirmDialog from "../../Common/ConfirmDialog";

export default function History({
    resultDataMap,
    setResultDataMap,
    resultFields = [],
    rows = [],
    showCalibrationGraph = true
}) {

    const [graphVisible, setGraphVisible] = useState(
        showCalibrationGraph
    );

    const [selectedRun, setSelectedRun] = useState(null);

    const [openClearDialog, setOpenClearDialog] = useState(false);

    const resultPanelRef = useRef(null);



/*
 * History data
 */
const historyData = useMemo(
    () => Object.values(resultDataMap || {}),
    [resultDataMap]
);


/*
 * Selected history entry
 *
 * This is based on the selected run.
 */
const selectedEntry = useMemo(() => {

    if (!selectedRun || historyData.length === 0) {
        return null;
    }

    return historyData.find(
        entry =>
            String(entry.run) === String(selectedRun)
    ) || null;

}, [historyData, selectedRun]);


/*
 * Automatically select the latest run after calibration.
 *
 * Also hides the graph when history is empty.
 */
useEffect(() => {

    if (historyData.length === 0) {

        setSelectedRun(null);
        setGraphVisible(false);

        return;
    }

    /*
     * Find latest run
     */
    const latestEntry = historyData.reduce(
        (latest, entry) => {

            if (!latest) {
                return entry;
            }

            return Number(entry.run) > Number(latest.run)
                ? entry
                : latest;
        },
        null
    );

    if (latestEntry?.run !== undefined) {

        setSelectedRun(latestEntry.run);

        setGraphVisible(true);
    }

    /*
     * Scroll to Result panel after calibration
     */
    const timer = setTimeout(() => {

        resultPanelRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });

    }, 150);

    return () => clearTimeout(timer);

}, [historyData]);




    /*
     * =========================================================
     * WHEN CALIBRATION RESULT / HISTORY CHANGES
     *
     * Automatically:
     * 1. Select latest run
     * 2. Show graph
     * 3. Scroll to Result panel
     * =========================================================
     */
    useEffect(() => {

        if (historyData.length === 0) {

            setSelectedRun(null);
            setGraphVisible(false);

            return;
        }


        /*
         * Find latest run.
         */
        const latestEntry = historyData.reduce(
            (latest, entry) =>
                Number(entry.run) > Number(latest.run)
                    ? entry
                    : latest,
            historyData[0]
        );


        if (latestEntry?.run !== undefined) {

            setSelectedRun(latestEntry.run);

            setGraphVisible(true);

        }


        /*
         * Scroll to Result panel.
         */
        const timer = setTimeout(() => {

            resultPanelRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });

        }, 150);


        return () => clearTimeout(timer);

    }, [historyData]);


    /*
     * =========================================================
     * PARSE RESPONSE VALUES
     *
     * Supports:
     *
     * 1.23
     * "1.23"
     * "1.23,1.24,1.25"
     * "1.23 1.24 1.25"
     * ["1.23", "1.24"]
     *
     * IMPORTANT:
     * 0 is a valid value.
     * =========================================================
     */
    const parseResponses = (value) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return [];
        }


        if (Array.isArray(value)) {

            return value
                .filter(
                    v =>
                        v !== null &&
                        v !== undefined &&
                        String(v).trim() !== ""
                )
                .map(v => Number(v))
                .filter(v => Number.isFinite(v));
        }


        return String(value)
            .split(/[,\s]+/)
            .map(v => v.trim())
            .filter(v => v !== "")
            .map(v => Number(v))
            .filter(v => Number.isFinite(v));
    };


    /*
     * =========================================================
     * RESP1 DATA
     *
     * X = Resp1
     * Y = SAV
     * =========================================================
     */
    const resp1Data = useMemo(() => {

        if (!Array.isArray(rows)) {
            return [];
        }

        const data = [];

        rows.forEach(row => {

            if (!row) {
                return;
            }


            /*
             * SAV = 0 is valid.
             */
            if (
                row.sav === null ||
                row.sav === undefined ||
                String(row.sav).trim() === ""
            ) {
                return;
            }


            const sav = Number(row.sav);

            if (!Number.isFinite(sav)) {
                return;
            }


            const responses = parseResponses(
                row.resp1
            );


            responses.forEach(response => {

                data.push([
                    response,
                    sav
                ]);

            });

        });

        return data;

    }, [rows]);


    /*
     * =========================================================
     * RESP2 DATA
     *
     * X = Resp2
     * Y = SAV
     * =========================================================
     */
    const resp2Data = useMemo(() => {

        if (!Array.isArray(rows)) {
            return [];
        }

        const data = [];

        rows.forEach(row => {

            if (!row) {
                return;
            }


            /*
             * SAV = 0 is valid.
             */
            if (
                row.sav === null ||
                row.sav === undefined ||
                String(row.sav).trim() === ""
            ) {
                return;
            }


            const sav = Number(row.sav);

            if (!Number.isFinite(sav)) {
                return;
            }


            const responses = parseResponses(
                row.resp2
            );


            responses.forEach(response => {

                data.push([
                    response,
                    sav
                ]);

            });

        });

        return data;

    }, [rows]);


    /*
     * =========================================================
     * CALCULATE TRENDLINE
     * =========================================================
     */
    const calculateTrendline = (data) => {

        if (
            !Array.isArray(data) ||
            data.length < 2
        ) {

            return {
                line: [],
                slope: 0,
                intercept: 0,
                r2: 0
            };

        }


        const n = data.length;


        const sumX = data.reduce(
            (sum, point) =>
                sum + point[0],
            0
        );


        const sumY = data.reduce(
            (sum, point) =>
                sum + point[1],
            0
        );


        const sumXY = data.reduce(
            (sum, point) =>
                sum +
                point[0] *
                point[1],
            0
        );


        const sumX2 = data.reduce(
            (sum, point) =>
                sum +
                point[0] *
                point[0],
            0
        );


        const denominator =
            n * sumX2 -
            sumX * sumX;


        /*
         * Cannot calculate trendline when
         * all X values are identical.
         */
        if (denominator === 0) {

            return {
                line: [],
                slope: 0,
                intercept: 0,
                r2: 0
            };

        }


        const slope =
            (
                n * sumXY -
                sumX * sumY
            ) /
            denominator;


        const intercept =
            (
                sumY -
                slope * sumX
            ) /
            n;


        /*
         * R�
         */
        const meanY = sumY / n;


        const ssTot = data.reduce(
            (sum, point) =>
                sum +
                Math.pow(
                    point[1] - meanY,
                    2
                ),
            0
        );


        const ssRes = data.reduce(
            (sum, point) => {

                const predicted =
                    slope * point[0] +
                    intercept;

                return (
                    sum +
                    Math.pow(
                        point[1] -
                        predicted,
                        2
                    )
                );

            },
            0
        );


        const r2 =
            ssTot === 0
                ? 1
                : 1 -
                ssRes / ssTot;


        /*
         * Trendline endpoints.
         */
        const xValues = data.map(
            point => point[0]
        );


        const minX = Math.min(
            ...xValues
        );


        const maxX = Math.max(
            ...xValues
        );


        const line = [
            [
                minX,
                slope * minX +
                intercept
            ],
            [
                maxX,
                slope * maxX +
                intercept
            ]
        ];


        return {
            line,
            slope,
            intercept,
            r2
        };

    };


    /*
     * =========================================================
     * TRENDLINES
     * =========================================================
     */
    const resp1Trend = useMemo(
        () =>
            calculateTrendline(
                resp1Data
            ),
        [resp1Data]
    );


    const resp2Trend = useMemo(
        () =>
            calculateTrendline(
                resp2Data
            ),
        [resp2Data]
    );


    /*
     * =========================================================
     * FORMAT NUMBER
     * =========================================================
     */
    const formatNumber = (value) => {

        if (!Number.isFinite(value)) {
            return "0";
        }

        return Number(value).toFixed(6);

    };


    /*
     * =========================================================
     * CHART OPTIONS
     *
     * Equation is displayed at TOP of chart.
     * No equation in the middle of graph.
     * =========================================================
     */
    const chartOptions = useMemo(() => {

        const getEquation = (
            label,
            trend
        ) => {

            if (
                !trend ||
                !Number.isFinite(
                    trend.slope
                )
            ) {
                return "";
            }


            return (
                `${ label }: y = ` +
                `${
    formatNumber(
        trend.slope
    )
} x ` +
                `${
    trend.intercept >= 0
    ? "+"
    : "-"
} ` +
                `${
    formatNumber(
        Math.abs(
            trend.intercept
        )
    )
} `
            );

        };


        return {

            chart: {
                type: "scatter",
                zoomType: "xy",
                height: 450,

                spacingTop: 45,

                events: {

                    render: function () {

                        const chart = this;


                        /*
                         * Remove previous label.
                         */
                        if (
                            chart.equationLabel
                        ) {

                            chart.equationLabel.destroy();

                            chart.equationLabel =
                                null;

                        }


                        const equations = [];


                        if (
                            resp1Data.length >= 2
                        ) {

                            equations.push(
                                getEquation(
                                    "Resp1",
                                    resp1Trend
                                )
                            );

                        }


                        if (
                            resp2Data.length >= 2
                        ) {

                            equations.push(
                                getEquation(
                                    "Resp2",
                                    resp2Trend
                                )
                            );

                        }


                        if (
                            equations.length === 0
                        ) {
                            return;
                        }


                        /*
                         * Create equation label.
                         */
                        chart.equationLabel =
                            chart.renderer
                                .text(
                                    equations.join(
                                        "     |     "
                                    ),
                                    0,
                                    0
                                )
                                .css({
                                    fontSize: "13px",
                                    fontWeight: "bold"
                                })
                                .add();


                        const bbox =
                            chart.equationLabel
                                .getBBox();


                        /*
                         * Center horizontally.
                         */
                        const x =
                            chart.plotLeft +
                            (
                                chart.plotWidth -
                                bbox.width
                            ) / 2;


                        /*
                         * TOP position.
                         */
                        const y =
                            chart.plotTop -
                            15;


                        chart.equationLabel.attr({
                            x,
                            y
                        });

                    }

                }

            },


            title: {
                text: "Cal Curve"
            },


            xAxis: {

                title: {
                    text: "Response"
                },

                gridLineWidth: 1

            },


            yAxis: {

                title: {
                    text: "SAV"
                },

                gridLineWidth: 1

            },


            legend: {
                enabled: true
            },


            tooltip: {

                useHTML: true,

               
formatter: function () {
    return `
                        <b> ${ this.series.name }</b> <br />
                    Response: ${ this.x } <br />
                    SAV: ${ this.y }
                    `;
}


            },


            plotOptions: {

                scatter: {

                    marker: {
                        radius: 5,
                        symbol: "circle"
                    }

                }

            },


            series: [

                /*
                 * RESP1
                 */
                {
                    name: "Resp1",

                    type: "scatter",

                    data: resp1Data,

                    marker: {
                        radius: 5,
                        symbol: "circle"
                    }
                },


                /*
                 * RESP1 TRENDLINE
                 */
                {
                    name: "Resp1 Trendline",

                    type: "line",

                    data:
                        resp1Trend.line,

                    marker: {
                        enabled: false
                    },

                    enableMouseTracking: false,

                    dashStyle: "Dash",

                    lineWidth: 2
                },


                /*
                 * RESP2
                 */
                {
                    name: "Resp2",

                    type: "scatter",

                    data: resp2Data,

                    marker: {
                        radius: 5,
                        symbol: "circle"
                    }
                },


                /*
                 * RESP2 TRENDLINE
                 */
                {
                    name: "Resp2 Trendline",

                    type: "line",

                    data:
                        resp2Trend.line,

                    marker: {
                        enabled: false
                    },

                    enableMouseTracking: false,

                    dashStyle: "Dash",

                    lineWidth: 2
                }

            ],


            credits: {
                enabled: false
            }

        };

    }, [
        resp1Data,
        resp2Data,
        resp1Trend,
        resp2Trend
    ]);


    /*
     * =========================================================
     * CLEAR HISTORY
     *
     * This also hides graph.
     * =========================================================
     */
    const handleClearHistory = () => {

        sessionStorage.removeItem(
            "microslideHistory_calibrate"
        );


        setResultDataMap({});


        setSelectedRun(null);


        setGraphVisible(false);


        setOpenClearDialog(false);

    };


    /*
     * =========================================================
     * SELECT RUN
     * =========================================================
     */
    const handleSelectRun = (entry) => {

        if (!entry) {
            return;
        }


        setSelectedRun(entry.run);


        /*
         * Show graph when a run is selected.
         */
        setGraphVisible(true);

    };


    /*
     * =========================================================
     * RENDER
     * =========================================================
     */
    return (

        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2
            }}
        >

            {/* =================================================
                RESULT + HISTORY
            ================================================== */}

            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        md: "row"
                    },
                    gap: 2,
                    alignItems: "stretch"
                }}
            >

                {/* =================================================
                    RESULT
                ================================================== */}

                <Paper
                    ref={resultPanelRef}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        p: 2,
                        minHeight: 300
                    }}
                >

                    <Typography
                        variant="h6"
                        gutterBottom
                    >
                        Result
                        {selectedEntry
                            ? ` (${ selectedEntry.description || "" })`
                            : ""
                        }
                    </Typography>


                    {selectedEntry?.result ? (

                        <TableContainer>

                            <Table size="small">

                                <TableBody>

                                    {resultFields.map(
                                        field => (

                                            <TableRow
                                                key={field}
                                            >

                                                <TableCell>
                                                    <strong>
                                                        {field}
                                                    </strong>
                                                </TableCell>

                                                <TableCell>
                                                    {
                                                        selectedEntry
                                                            .result?.[
                                                            field
                                                        ] ?? "-"
                                                    }
                                                </TableCell>

                                            </TableRow>

                                        )
                                    )}

                                </TableBody>

                            </Table>

                        </TableContainer>

                    ) : (

                        <Typography
                            color="text.secondary"
                        >
                            {historyData.length === 0
                                ? "No Result Data"
                                : "Select a history run to view result."
                            }
                        </Typography>

                    )}

                </Paper>


                {/* =================================================
                    HISTORY
                ================================================== */}

                <Paper
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        p: 2,
                        minHeight: 300
                    }}
                >

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center",
                            mb: 2
                        }}
                    >

                        <Typography variant="h6">
                            History
                        </Typography>


                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={
                                <DeleteSweepIcon />
                            }
                            disabled={
                                historyData.length === 0
                            }
                            onClick={() =>
                                setOpenClearDialog(
                                    true
                                )
                            }
                        >
                            Clear History
                        </Button>

                    </Box>


                    {historyData.length > 0 ? (

                        <TableContainer
                            sx={{
                                maxHeight: 300,
                                overflow: "auto"
                            }}
                        >

                            <Table
                                size="small"
                                stickyHeader
                            >

                                <TableHead>

                                    <TableRow>

                                        <TableCell>
                                            <strong>
                                                Run
                                            </strong>
                                        </TableCell>

                                        <TableCell>
                                            <strong>
                                                Description
                                            </strong>
                                        </TableCell>

                                        <TableCell align="center">
                                            <strong>
                                                Predict
                                            </strong>
                                        </TableCell>

                                    </TableRow>

                                </TableHead>


                                <TableBody>

                                    {historyData.map(
                                        entry => (

                                            <TableRow
                                                key={entry.run}
                                                hover
                                                selected={
                                                    String(
                                                        selectedRun
                                                    ) ===
                                                    String(
                                                        entry.run
                                                    )
                                                }
                                                sx={{
                                                    cursor:
                                                        "pointer"
                                                }}
                                                onClick={() =>
                                                    handleSelectRun(
                                                        entry
                                                    )
                                                }
                                            >

                                                <TableCell>

                                                    <Link
                                                        component="button"
                                                        underline="hover"
                                                    >
                                                        {
                                                            entry.run
                                                        }
                                                    </Link>

                                                </TableCell>


                                                <TableCell>
                                                    {
                                                        entry.description
                                                    }
                                                </TableCell>


                                                <TableCell
                                                    align="center"
                                                >

                                                    <Link
                                                        component="button"
                                                        underline="hover"
                                                        onClick={e => {

                                                            e.stopPropagation();

                                                            /*
                                                             * Keep existing
                                                             * prediction launch
                                                             * logic here.
                                                             */

                                                        }}
                                                    >
                                                        Launch
                                                    </Link>

                                                </TableCell>

                                            </TableRow>

                                        )
                                    )}

                                </TableBody>

                            </Table>

                        </TableContainer>

                    ) : (

                        <Typography
                            color="text.secondary"
                        >
                            No History
                        </Typography>

                    )}

                </Paper>

            </Box>


            {/* =================================================
                GRAPH
            ================================================== */}

            {graphVisible && (

                <Paper
                    sx={{
                        width: "100%",
                        p: 2,
                        boxSizing: "border-box"
                    }}
                >

                    <Typography
                        variant="h6"
                        gutterBottom
                    >
                        Calibration Graph
                    </Typography>


                    {resp1Data.length === 0 &&
                    resp2Data.length === 0 ? (

                        <Typography
                            color="text.secondary"
                        >
                            No valid calibration response
                            data available for graph.
                        </Typography>

                    ) : (

                        <Box
                            sx={{
                                width: "100%",
                                minHeight: 450
                            }}
                        >

                            <HighchartsReact
                                highcharts={Highcharts}
                                options={chartOptions}
                                containerProps={{
                                    style: {
                                        width: "100%",
                                        height: "450px"
                                    }
                                }}
                            />

                        </Box>

                    )}

                </Paper>

            )}


            {/* =================================================
                CLEAR HISTORY DIALOG
            ================================================== */}

            <ConfirmDialog
                open={openClearDialog}
                title="Clear History"
                message="Are you sure you want to clear all calibration history?"
                onConfirm={
                    handleClearHistory
                }
                onCancel={() =>
                    setOpenClearDialog(
                        false
                    )
                }
            />

        </Box>
    );
}

